<template>
  <div class="return-item">
    <Card :outlined="true" :class="{ 'margin-bottom-s': returnItemsEditable?.returnItems }" padding-class="padding-s">
      <ProductThumbnailWrapper
        :products="[product] ?? []"
        show-policy-message
        hide-item-border
        page="item-selection"
        :image-width="95"
        :aspect-ratio="0.9"
        :fetchpriority="priority"
        :loading="priority === 'high' ? 'eager' : 'lazy'"
      />
      <button v-if="isMcom && isMarketplaceOrder" id="contact-seller" class="link-body" @click="openContactSellerSlideout">Contact Seller</button>

      <template v-if="!returnItemsEditable?.returnItems">
        <div class="margin-top-xs">{{ config.returnItems.previewReturnReason }}{{ selectedReturnReason }}</div>
        <div v-if="product.returnQuantity > 1">{{ config.returnItems.previewReturnQuantity }}{{ selectedQuantity }}</div>
      </template>
    </Card>
    <div v-if="returnItemsEditable?.returnItems">
      <Dropdown
        :id="`select-return-reason-${product?.key}`"
        v-model="returnReason"
        :label="config.returnItems.returnReason"
        name="returnReason"
        :disabled="product.virtualReturnItem || isPrepareDeclarationLoading"
        :options="returnReasonsOptions"
        :required="false"
        @change="onDropdownChange($event)"
      />
    </div>
    <div v-if="returnItemsEditable?.returnItems && product?.returnQuantity > 1" class="margin-top-s">
      <Dropdown
        :id="`select-return-quantity-${product?.key}`"
        v-model="returnQuantity"
        :label="config.returnItems.quantity"
        name="quantity"
        :disabled="product.virtualReturnItem || isPrepareDeclarationLoading"
        :options="returnQuantityOptions"
        :required="false"
        @change="onDropdownChange($event)"
      />
    </div>
    <div v-if="showRemove" class="remove-item-section padding-top-xxs">
      <button class="link-body" @click="removeProduct">Remove</button>
    </div>
  </div>
</template>
<script setup>
import { defineAsyncComponent, markRaw, ref, computed, watch } from 'vue';
import Card from '@atomic-ui/card/Card';
import Dropdown from '@atomic-ui/dropdown/Dropdown';
import ProductThumbnailWrapper from '@/components/common/ProductThumbnailWrapper';
import { useSlideout } from '@/composables/useSlideout';
import { useModal } from '@/composables/useModal';
import useBrand from '@/composables/useBrand';
import * as config from 'config';

const props = defineProps({
  product: {
    type: Object,
    default() {
      return {};
    },
  },
  hasMultipleReturnItems: {
    type: Boolean,
    default: false,
  },
  hasUpcNumber: {
    type: Boolean,
    default: false,
  },
  priority: {
    type: String,
    default: 'auto',
  },
});
const { isMcom } = useBrand();

// Emit events
const emit = defineEmits(['calculate-estimated-refund']);
const returnItemStore = useReturnItemStore();
const { registerSlideout, toggleSlideout } = useSlideout();
const { registerModal, toggleModal } = useModal();

const returnReason = ref(props.product?.virtualReturnItem ? props.product?.returnReason?.code : '');
const returnQuantity = ref(props.product?.virtualReturnItem ? props.product?.returnQuantity?.toString() : null);
const shouldShowDamagedBatteryModal = ref(true);

const isMarketplaceOrder = computed(() => returnItemStore.IS_MARKETPLACE_ORDER);
const returnItemsEditable = computed(() => returnItemStore.returnItemsEditable);
const returnSelections = computed(() => returnItemStore.returnSelections);
const returnReasons = computed(() => returnItemStore.returnItems?.returnReason);
const isPrepareDeclarationLoading = computed(() => returnItemStore.loading.prepareDeclarationResponse);

const selectedReturnReason = computed(() => {
  if (props.product?.virtualReturnItem) {
    return props.product?.returnReason?.description;
  }
  const selectedItem = returnSelections.value?.returnItems?.find((item) => item.key === props.product?.key);
  return returnReasons.value?.find((reason) => reason.code === selectedItem.returnReason)?.description ?? '';
});

const returnReasonsOptions = computed(() => {
  if (props.product?.virtualReturnItem) {
    return [
      {
        value: props.product?.returnReason?.code,
        displayValue: props.product?.returnReason?.description,
      },
    ];
  }
  return [
    {
      displayValue: 'Select a Return Reason',
      disabled: !!returnReason.value,
      value: '',
    },
    ...((Array.isArray(returnReasons.value) &&
      returnReasons.value.map((reason) => ({
        value: reason?.code,
        displayValue: reason?.description,
      }))) ||
      []),
  ];
});

const returnQuantityOptions = computed(() => {
  if (props.product?.virtualReturnItem) {
    return [
      {
        value: props.product?.returnQuantity,
        displayValue: props.product?.returnQuantity,
      },
    ];
  }
  return [
    {
      displayValue: 'Select a Quantity',
      disabled: !!returnQuantity.value,
      value: '',
    },
    ...new Array(props.product?.returnQuantity).fill().map((_, index) => ({
      value: index + 1,
      displayValue: index + 1,
    })),
  ];
});

const selectedQuantity = computed(() => {
  if (props.product?.virtualReturnItem) {
    return props.product?.returnQuantity;
  }
  return returnSelections.value?.returnItems?.find((item) => item.key === props.product?.key)?.quantity ?? 0;
});
const contactSellerData = computed(() => ({
  sellerName: props.product?.mkpLineItemDetails?.sellerName,
  partnerFulfillmentId: props.product?.mkpLineItemDetails?.partnerFulfillmentId,
  lineNumber: props.product?.lineNumber,
  mkpReturnReasons: returnItemStore.returnItems.mkpReturnReasons,
  orderNumber: returnItemStore.returnItems?.orderNumber,
  productId: props.product?.id,
  upcNumber: props.product?.upc,
}));
const showRemove = computed(() => {
  const hasReturnItemsEditable = returnItemsEditable.value?.returnItems;
  return (
    (!props.hasUpcNumber && hasReturnItemsEditable && props.hasMultipleReturnItems) ||
    (props.hasUpcNumber && hasReturnItemsEditable && returnSelections.value?.returnItemsDraft?.length > 1)
  );
});

const v2SubmitReturnError = computed(() => returnItemStore.errors.v2SubmitReturnError);

const showDamagedBatteryModal = () => {
  registerModal({
    id: 'damaged-battery',
    title: 'Is the lithium battery damaged?',
    width: '628px',
    props: {
      data: contactSellerData.value,
    },
    component: markRaw(defineAsyncComponent(() => import('@/components/returns/marketplace/DamagedBattery'))),
  });
  toggleModal('damaged-battery');
};

const onDropdownChange = async (event) => {
  const items = [...(returnSelections.value.returnItemsDraft || [])];
  const selectedItemIndex = items?.findIndex((item) => item.key === props.product?.key);
  let selectedItem = items?.find((item) => item.key === props.product?.key);
  selectedItem = {
    ...selectedItem,
    [event.target.name]: event.target.value,
  };
  items.splice(selectedItemIndex, 1, selectedItem);
  returnItemStore.SET_RETURN_SELECTIONS({ returnItemsDraft: items });
  const isReturnReasonSelected = event.target.name === 'returnReason';
  if (isMcom && isReturnReasonSelected && isMarketplaceOrder.value && props.product?.isHazmatItem && shouldShowDamagedBatteryModal.value) {
    showDamagedBatteryModal();
    shouldShowDamagedBatteryModal.value = false;
  }
  if (event.target.name === 'quantity' || props.product?.returnQuantity === 1) {
    emit('calculate-estimated-refund');
  }
};

const removeProduct = () => {
  const selectedReturnItems = returnSelections.value?.returnItemsDraft;
  const removeItemIndex = selectedReturnItems.findIndex((item) => item.key === props.product?.key);
  if (removeItemIndex !== -1) {
    selectedReturnItems.splice(removeItemIndex, 1);
    returnItemStore.SET_RETURN_SELECTIONS({ returnItemsDraft: selectedReturnItems });
    emit('calculate-estimated-refund');
  }
};

const openContactSellerSlideout = () => {
  registerSlideout({
    id: 'item-details-slideout',
    title: 'Contact Seller',
    props: {
      data: contactSellerData.value,
    },
    component: markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "contact-seller" */ '@/components/marketplace/ContactSeller'))),
  });
  toggleSlideout('item-details-slideout');
};

watch(
  () => v2SubmitReturnError.value,
  () => {
    if (v2SubmitReturnError.value) {
      returnReason.value = '';
      returnQuantity.value = '';
    }
  },
);
</script>

<style brand="bcom" lang="scss" scoped>
::v-deep(.card.selected) {
  border-width: rem-calc(1);
}
</style>

<style lang="scss" scoped>
.return-item:not(:first-child) {
  margin-top: rem-calc(32);
}
</style>
